// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import { executePolling } from '../../../../share/util/api/polling';
import { executeSyncRequest } from '../../../../share/util/api/syncRequest';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { OBD_ALL_DTC } from '../../../../constants/usageCountTypes';
import apiService from '../../../../share/util/api/service';
import alert from '../../../../share/alert';
import loading from '../../../../share/components/vue/ProcessingIcon';
import util from '../../../../share/util/utils';
import eventLogUtil from '../../../../share/util/eventLogUtil';
import cstmTable from '../../../../share/components/vue/PaginationTable';
import ffdModal from '../../../vue/FFDModal';
import diagnoseResource from '../../../../share/util/diagnose';
import memoPage from '../../../vue/MemoPage';
import customProgress from "../../../../share/components/vue/CustomProgress";
import { compareSystemName } from "../../../../util/obd/system";
import { pushObdTop } from '../../../../router/obd';
import { createObdDtcTable, getMemoryClearResultMessage, getDtcStatusResource } from '../../../../util/obd/diag';
import { OBD_ALL_DTC_LIMIT, OBD_FFD_LATEST_LIMIT, OBD_FFD_OLDEST_LIMIT } from '../../../../util/obd/functions';
import { getMeasuredDataName, getCsvFileName } from '../../../../util/fileExport';


export default {
  name: "obdAllDtc",
  data() {
    return {
      processing: false,
      measuredId: null,
      getDiagCodeListResponse: null,
      dtcSystemTableColumns: [],
      dtcListItems: [],
      isDisabledMemo: false,
      progress: 0,
      phaseMessage: 'message.label_vehicle_connect_diag_code',
      modalProgress: false,
      isObd: true,
      // FFD一覧
      modalFfd: false,
      ffdTitle: '',
      ffdSystem: '',
      ffdDescription: '',
      ffdSystemTable: [],
      selectedSystem: null,
      // ボタン消去
      isInvisibleChainButton: true,
      // vue warn回避
      readinessCodeTable: [],
      readinessCodeTableColumns: [],
      isVisibleReadinessCodeButton: false,
      isEnableClearDtc: true,
      modalReadiness: false,
      modalMemo: false,
      isMemoDialogLoading: false,
      readinessCodeInputDtc: '',
      readinessCodeValidateDtc: {},
      isInvisibleClearDtcButton: false,
    };
  },

  props: {
    obdFunctionLabelKey: { type: String, required: false, default: 'fixed_resource.obd_menu' },
    functionNameKey: { type: String, required: false, default: 'fixed_resource.obd_menu_alldtc' },
    destinationAPI: { type: String, required: false, default: apiService.OBD },
    testModeVisible: { type: Boolean, required: false, default: false }
  },

  components: {
    'processing-view': loading,
    'cstm-table': cstmTable,
    'ffd-modal': ffdModal,
    'memo-Page': memoPage,
    'custom-Progress': customProgress,
  },

  watch: {
    language: {
      // 言語変更を監視する
      handler(val) {
        val; // To avoid warning
        this.updateColumns();
        this.updateDTCTable();
      }
    },
  },

  methods: {
    ...mapActions('cache', ['cacheFFDStores']),
    ...mapActions('auth', ['logout', 'resetSession']),
    ...mapActions('observation', ['usageCountUp']),

    // componentのmounted時に呼び出す処理
    mountedLogic() {
      this.dtcListItems = [];
      this.cacheFFDStores([]);
      this.getDiagCodeRequest(false);
      // パスに"menu"を含まない場合はメモボタンを無効化
      if (!this.$route.path.includes('menu')) {
        this.isDisabledMemo = true;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * 非同期でダイアグ取得を実行し、テーブル更新を行う
     * @param {boolean} eventLogHandleIdFlg 再取得から呼ばれた際に true（イベントログの操作 ID 発行する）とする
     * @returns なし
     */
    async getDiagCodeRequest(eventLogHandleIdFlg) {
      try {
        this.progress = 0;
        this.modalProgress = true;
        if (eventLogHandleIdFlg) {
          // イベントログの操作 ID 発行
          await eventLogUtil.createEventLogHandleId(this.netAppId);
        }
        this.progress = 25;
        const response = await executePolling(
          this,
          // POST API 呼び出しのコールバック
          async () => {
            return await apiService.postCall(
              apiService.OBD,
              `/netApps/${this.netAppId}/obd/${this.obdDataId}/allDtc`
            );
          },
          // GET API 呼び出しのコールバック
          async (postApiResponse) => {
            return await apiService.getCall(
              apiService.OBD,
              `/netApps/${this.netAppId}/requests/${postApiResponse.data.request_id}`,
              this.operationScreen
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // GET API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // setPollingTimeOutCallback
          () => { },
          // pollingRetryCount
          120,
          // pollingRetryInterval
          null
        );

        if (response) {
          this.progress = 75;
          // OBDシステムの利用回数カウントアップ
          this.usageCountUp({ type: OBD_ALL_DTC });
          // 正常終了：ダイアグコードの一覧取得を行う
          this.measuredId = response.data.results.measure_id;
          const parseFFDErrorSystems = response.data.results?.parse_ffd_error_systems || [];
          this.getDiagCodeListResponse = await this.getDiagCodeList();
          // responseが存在しない場合、処理終了
          if (!this.getDiagCodeListResponse) return;
          this.updateDTCTable();
          this.updateColumns();
          this.progress = 100;

          if (parseFFDErrorSystems.length > 0) this.showParseFFDError(parseFFDErrorSystems);
        }
      } finally {
        this.modalProgress = false;
      }
    },

    /**
     * 一覧取得処理を行う関数
     * @returns GetのResponse | null
     */
    async getDiagCodeList() {
      let results = null;
      let response = null;
      const params = { last_evaluated_key: null };
      do {
        // Get APIを呼び出す
        response = await executeSyncRequest(
          this,
          async () => {
            return await apiService.getParamCall(
              apiService.OBD,
              `/obd/${this.obdDataId}/measures/${this.measuredId}/dtc`,
              params,
              this.operationScreen
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // GET API で正常レスポンスが返ってきた際の内容チェックはデフォルト処理を利用
          undefined,
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する
          (result) => { this.validateErrorResult(result); },
        );
        if (response) {
          results = results ? results.concat(response.data.data) : response.data.data;
          if (results?.length >= OBD_ALL_DTC_LIMIT) break;
          params.last_evaluated_key = response.data.last_evaluated_key ? JSON.stringify(response.data.last_evaluated_key) : null;
        }
      } while (params.last_evaluated_key);
      // 処理失敗時は response が null になるので考慮
      if (results) {
        response.data.data = results.length >= OBD_ALL_DTC_LIMIT ? results.slice(0, OBD_ALL_DTC_LIMIT) : results;
        return response.data;
      }
      else {
        return null;
      }
    },

    /**
     * FFD 解析失敗エラー発生時の処理を行う
     * @param {Array<string>} FFD 解析失敗したシステム No
     */
    showParseFFDError(parseFFDErrorSystems) {
      const systemNames = parseFFDErrorSystems.map(
        (systemNo) =>
          this.systems.find((system) => system.systemNo === systemNo).systemName
      );
      const messages = this.$t(
        "fixed_resource.obd_function_all_dtc_ffd_error",
        [systemNames.join("\n")]
      );
      alert.showError(this, null, messages); // OK 押下後は何もしない（取得できた範囲で表示する
    },

    // その他のエラー発生時の処理を行う
    showUnexpectedError() {
      const messages = this.$t('fixed_resource.obd_function_common_message_result_ng_2');
      alert.showError(this, null, messages,
        () => { pushObdTop(this); }
      );
    },

    // メモダイアログを表示する
    openNoteModal() {
      this.modalMemo = true;
    },

    /**
     * メモダイアログを閉じる
     */
    closeNoteModal() {
      this.modalMemo = false;
    },

    // メモ情報の保存
    async memoSave() {
      await this.$refs.memo.save();
    },

    // メモ情報のクリア
    memoClear: function () {
      this.$refs.memo.clear();
    },

    // 再取得ボタン押下時の処理を行う
    async reGetDTC() {
      const messages = this.$t('individual.message_diag_code_confirm_get_information');
      alert.showConfirmation(this, null, messages,
        () => {
          this.cacheFFDStores([]);
          // 全DTCの取得
          this.getDiagCodeRequest(true);
        }
      );
    },

    /**
     * FFD ボタン押下時の処理を行う
     * @param {object} data 押下したデータ
     */
    async clickFFD(data) {
      if (!data) return;
      const system = this.systems.filter(e => e.systemNo === data.systemID);
      // タイトルの設定
      const status = data.status ? ` (${getDtcStatusResource(data.status, this)}) ` : '';
      this.ffdTitle = data.dtc + status;
      this.ffdSystem = system[0]?.systemName || '';
      this.ffdDescription = this.$t(data.description);
      const cache = this.cachedFFDs.filter(e => e.system === data.systemID && e.dtc === data.dtc);
      if (cache.length > 0) {
        // キャッシュ有りならキャッシュ済みデータを表示
        this.ffdSystemTable = cache[0].data;
        this.selectedSystem = data;
        this.modalFfd = true;
      } else {
        // キャッシュなしなら FFD 一覧情報を取得
        const ffdResponse = await this.getFfdResponse(data.dtc, data.systemID);
        // responseが存在しない場合、処理終了
        if (!ffdResponse) return;
        // キャッシュの更新
        this.cacheFFDStores([...this.cachedFFDs, { system: data.systemID, dtc: data.dtc, data: ffdResponse }]);
        // FFDダイアログの表示
        this.ffdSystemTable = ffdResponse;
        this.selectedSystem = data;
        this.modalFfd = true;
      }
    },

    /**
     * FFD一覧情報取得を行う関数
     * @param {string} dtc 選択したdtc
     * @param {string} systemNo systemNo
     */
    async getFfdResponse(dtc, systemNo) {
      try {
        let latest_results = [];
        let oldest_results = [];
        let response = null;
        const params = { last_evaluated_key: null };
        this.processing = true;
        do {
          // Get APIを呼び出す
          response = await executeSyncRequest(
            this,
            async () => {
              return await apiService.getParamCall(
                apiService.OBD,
                `/obd/${this.obdDataId}/measures/${this.measuredId}/ffd/systems/${systemNo}/codes/${dtc}`,
                params,
                this.operationScreen
              );
            },
            // エラー発生のコールバック
            (errorType) => { errorType; this.showUnexpectedError(); },
            // GET API で正常レスポンスが返ってきた際の内容チェックはデフォルト処理を利用
            undefined,
            // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する
            (result) => { this.validateErrorResult(result); },
          );
          if (response) {
            if (latest_results.length < OBD_FFD_LATEST_LIMIT) latest_results = latest_results.concat(response.data.latest);
            if (oldest_results.length < OBD_FFD_OLDEST_LIMIT) oldest_results = oldest_results.concat(response.data.oldest);
            if (latest_results.length >= OBD_FFD_LATEST_LIMIT && oldest_results.length >= OBD_FFD_OLDEST_LIMIT) break;
            params.last_evaluated_key = response.data.last_evaluated_key ? JSON.stringify(response.data.last_evaluated_key) : null;
          }
        } while (params.last_evaluated_key);
        // 処理失敗時は response が null になるので考慮
        if (latest_results.length > 0 || oldest_results.length > 0) {
          if (latest_results.length >= OBD_FFD_LATEST_LIMIT) {
            latest_results = latest_results.slice(0, OBD_FFD_LATEST_LIMIT);
          }
          if (oldest_results.length >= OBD_FFD_OLDEST_LIMIT) {
            oldest_results = oldest_results.slice(0, OBD_FFD_OLDEST_LIMIT);
          }
          return latest_results.concat(oldest_results);
        }
        else {
          return null;
        }
      } finally {
        this.processing = false;
      }
    },

    /**
     * カラムを作り直す
     */
    updateColumns() {
      // ダイアグコードレスポンスが空の場合、return
      if (!this.getDiagCodeListResponse) return;
      const sortSystems = (param) => {
        if (!param.data) return null;
        return [...param.data].sort((a, b) => compareSystemName(a.name, b.name, param.sortDir));
      };

      this.dtcSystemTableColumns = [
        { name: 'name', label: 'fixed_resource.header_vehicle_table_system_name', originalSortFunc: sortSystems },
        { name: 'status', label: 'fixed_resource.header_vehicle_dtc_table_status' },
        { name: 'dtc', label: 'fixed_resource.header_vehicle_dtc_table_code', originalSortFunc: diagnoseResource.sortFuncCode },
        { name: 'description', label: 'fixed_resource.header_vehicle_dtc_table_description', useResource: true, originalSortFunc: diagnoseResource.sortFuncDescription },
        { name: 'igonCount', label: 'fixed_resource.header_vehicle_dtc_table_igon_counts' },
        { name: 'igonTime', label: 'fixed_resource.header_vehicle_dtc_table_igon_times' },
        { name: 'counterType', label: 'fixed_resource.header_vehicle_dtc_table_type', useResource: true },
        { name: 'ffd', label: 'fixed_resource.header_vehicle_dtc_table_ffd', useButton: true, useResource: true, originalSortFunc: diagnoseResource.sortFuncFfd },
      ];
    },

    // テーブル情報更新処理を行う
    updateDTCTable() {
      let dtcListItems = [];
      // システムごとに処理
      for (const system of this.systems) {
        // システムに紐づく DTC があるかチェックする
        const dtcList = this.getDiagCodeListResponse.data.filter(dtc => dtc.system === system.systemNo);
        // グループ化されたDTC情報から、テーブルを作成する
        const targetDtcList = createObdDtcTable(dtcList, system.systemNo, system.systemName, this);
        // 作成したテーブル項目を dtcListItems に追加
        dtcListItems = dtcListItems.concat(targetDtcList);
      }
      this.dtcListItems = dtcListItems;
    },

    // DTC情報をCSVエクスポートする
    async csvExport() {
      const fileInfo = {};
      let resultInfo = {};
      // 行のフォーマット
      resultInfo = this.dtcListItems.map(item => {
        return {
          ...item,
          status: item.status ? getDtcStatusResource(item.status, this) : '',
          description: this.$t(item.description),
          counterType: this.$t(item.counterType),
          ffd: item.buttonVisible ? this.$t('message.available_ffd') : this.$t('message.no_ffd')
        };
      });
      // 列のフォーマット
      fileInfo.column = this.dtcSystemTableColumns.map(column => {
        if (column.name === 'igonCount') {
          column.label = 'fixed_resource.header_vehicle_dtc_table_igon_counts';
        } else if (column.name === 'igonTime') {
          column.label = 'fixed_resource.header_vehicle_dtc_table_igon_times';
        }
        return column;
      });
      // VINデータのフォーマット
      // Start画面から遷移した場合は、VIN取得をしないため、空文字で出力する。
      const vinData = this.isDisabledMemo ? '' : (this.vehicleInfo?.vehicle?.vin?.vin || '');
      const vinHeader = `"${this.$t('message.label_measured_data_search_vin')}","${vinData}"\n`;
      // 出力形式の設定
      const headers = fileInfo.column.map(column => {
        return { id: column.name, title: this.$t(column.label) };
      });
      // 説明・故障部位のエスケープ処理
      fileInfo.record = util.escapeDoubleQuotation(resultInfo, 'description');

      let endPoint = '';
      let measuredDataName = '';

      if (!this.isDisabledMemo && this.vehicleInfo.vehicle.project.id !== null && this.measuredId !== null) {
        try {
          this.processing = true;
          // メモボタン活性（パスに"menu"が存在）かつプロジェクトIDおよび計測データIDが存在する場合、計測データ取得APIを指定
          endPoint = '/projects/' + this.vehicleInfo.vehicle.project.id + '/' + this.measuredId;
          // 計測データ名を取得
          measuredDataName = await getMeasuredDataName(this, this.destinationAPI, endPoint, this.operationScreen);
        } finally {
          this.processing = false;
        }
      }
      const fileName = getCsvFileName(
        `${this.$t(this.functionNameKey)}${this.$t(this.obdFunctionLabelKey)}`,
        measuredDataName,
        this.isDisabledMemo ? '' : (this.vehicleInfo?.vehicle?.vin?.vin || '')
      );

      // CSVファイルエクスポート処理
      const data = vinHeader + util.createCsvData(headers, fileInfo.record);
      util.csvFileExport(data, fileName);
    },

    deleteDTC() {
      // 強調文言
      const sentence = '<font color="red">' + this.$t('fixed_resource.obd_function_dtcclear_confirm_message') + '</font>';
      const messages = this.$t('fixed_resource.obd_function_dtcclear_message', [sentence]);
      alert.showConfirmation(this, null, messages,
        async () => {
          this.cacheFFDStores([]);
          this.executeClearAction();
        });
    },

    // メモリクリアの実行
    async executeClearAction() {
      try {
        this.processing = true;
        // イベントログの操作 ID 発行
        await eventLogUtil.createEventLogHandleId(this.netAppId);
        const response = await executePolling(
          this,
          // POST API 呼び出しのコールバック
          async () => {
            return await apiService.postCall(
              apiService.OBD,
              `/netApps/${this.netAppId}/obd/${this.obdDataId}/clearDtc`,
              {},
              this.operationScreen
            );
          },
          // GET API 呼び出しのコールバック
          async (postApiResponse) => {
            return await apiService.getCall(
              apiService.OBD,
              `/netApps/${this.netAppId}/requests/${postApiResponse.data.request_id}`,
              this.operationScreen
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // GET API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        // 消去の正否に関わらず、削除結果画面を表示する
        if (response) this.resultMemoryClear(response.data);
      } finally {
        this.processing = false;
      }
    },

    // ダイアグ削除の結果画面の表示
    resultMemoryClear(response) {
      const failureClearSystems = [];
      util.deepSearch(response.results, 'is_clear_dtc_success', (key, val) => val === false, failureClearSystems);

      let failureClearSystemNames = null;
      if (failureClearSystems) {
        failureClearSystemNames = failureClearSystems.map(failureClearSystem => {
          return this.systems.find(e => e.systemNo === failureClearSystem.system_name)?.systemName;
        });
      }

      const messages = getMemoryClearResultMessage(this, failureClearSystemNames);
      alert.showInfo(this, null, messages,
        async () => {
          // DTCデータの再取得
          this.getDiagCodeRequest(true);
        }
      );
    },

    // vue warn 回避
    onClickAddDtcReadinessCode() { return; },
    deleteRowReadinessCode() { return; },
    allDeleteReadinessCode() { return; },
    readinessCodeCsvExport() { return; },
    onClickDecisionReadinessCode() { return; },
  },

  computed: {
    ...mapState({
      vehicleInfo: state => state.vehicle.vehicleInfo,
      language: state => state.systems.language,                  // メニューサポートの情報
      netAppId: state => state.netAppConnector.pairingAppId,      // クラウドコネクターの接続状況
      cachedFFDs: state => state.cache.ffdStores,
      obdDataId: state => state.obd.obdDataId,                    // OBD Data ID の取得結果
      systems: state => state.obd.systems,
    }),

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },

    /** プロジェクトID */
    projectId() {
      return this.vehicleInfo?.vehicle?.project?.id || '';
    }
  },

  mounted() {
    this.mountedLogic();
  },
};